import React, { useEffect, useMemo, useState } from 'react';

import { Notification, Spinner } from '@ergeon/core-components';

interface EmailRendererProps {
  html?: string;
  text?: string;
}

// Base styles for plain text emails only
const PLAIN_TEXT_STYLES = `
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: #333;
    margin: 16px;
    word-wrap: break-word;
    white-space: pre-wrap;
  }
  a {
    color: #0066cc;
  }
`;

/**
 * Renders email content safely in a sandboxed iframe.
 * Security is provided by the iframe sandbox attribute which blocks:
 * - JavaScript execution
 * - Form submissions
 * - Access to parent window
 * - Plugins
 *
 * Prefers HTML content but falls back to plain text.
 */
export const EmailRenderer: React.FC<EmailRendererProps> = ({ html, text }) => {
  const [loading, setLoading] = useState(true);

  const srcdoc = useMemo(() => {
    if (html) {
      // Parse HTML to modify links to open in new tab
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Force all links to open in new tab
      doc.querySelectorAll('a').forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
      });

      // Return the full document HTML, preserving all original styles
      return doc.documentElement.outerHTML;
    } else if (text) {
      // Escape plain text for safe rendering
      const escapedText = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');

      return `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <style>${PLAIN_TEXT_STYLES}</style>
          </head>
          <body>${escapedText}</body>
        </html>
      `;
    }
    return null;
  }, [html, text]);

  // Reset loading state when content changes
  useEffect(() => {
    setLoading(true);
  }, [srcdoc]);

  if (!srcdoc) {
    return (
      <Notification mode="embed" type="Information">
        No email content available
      </Notification>
    );
  }

  return (
    <div className="relative">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white dark:bg-gray-800 rounded">
          <Spinner active color="lightgray" size={40} />
        </div>
      )}
      <iframe
        className="w-full min-h-[400px] border-0 bg-white rounded"
        onLoad={() => setLoading(false)}
        sandbox="allow-popups allow-popups-to-escape-sandbox"
        srcDoc={srcdoc}
        title="Email content" />
    </div>
  );
};
